import { submergedRockTexture } from "./submerged_rock_art"
import { riverSample } from "game-common/room/river"
import type { WaterContact } from "./water_layer"
import { contentSheetResource, contentTexture, contentTextureGeneration } from "./content_textures"
import { Container, Sprite, Texture, Loader, Graphics, IPointData } from "pixi.js"
import { Bounds, NumberedTileMeta, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { roughToFineCoordinates, setRoughToFineCoordinates } from "game-common/util"
import { CoordinatePool } from "game-common/coordinate_pool"
import { TileTextureExtractor } from "./sprites/tile_texture_extractor"
import { occluderFadePolicy } from "./entity_tile_occluder_fade"
import { GrassFloor, GrassSway } from "./effects/grass_sway"
import { bonecallerStoneFrames, bonecallerStoneEnergy } from "./effects/bonecaller_stone"
import { isOreMaterial, oreMineralTexture } from "./effects/ore_mineral_art"
import { getRendererApp } from "./renderer_app"
import { isTreeEntityTile } from "game-common/map/map"
import { isEmberBushComposite } from "game-common/room/ember/forest_bushes"
import { BonecallerRitual } from "game-common/bonecaller_combat"
// Where a composite sprite goes and how deep it sorts: shared with Sprite Lab, so it lives on its own.
import { compositeSpritePlacement } from "./composite_tile_placement"
import { rootGroundPoint, treeRootEdges, treeRootFringeTexture } from "./effects/tree_root_fringe"
import { grassAt } from "./effects/grass_feet"
import { dirtZoneAt } from "./effects/dirt_ground"
import { treeRootSoilTexture } from "./effects/tree_root_soil"
import { hillHeightOf } from "./hill_lift"

/** A tile sprite drawn up for the hill under it, by `hillLift` px (0 off a hill). */
export type HillLifted = Sprite & { hillLift?: number }


export interface CompositeTileRendererOptions {
    surface: Container
    skySpriteSurfaceAbove: Container
    tilemapMetaProvider: TileMapMetaProvider
    compositeTileSpriteTextures: Map<string, Texture>
    surfaceObjectsByKey: Map<string, any>
    attachTexture: (texture: Texture, surface?: Container) => Sprite
    pertubation: (tileId: string, x: number, y: number, i: number, key: string, multiplier?: number) => number
    pctChance: (chance: number, key: string) => boolean
    logic: any
    tileTextureExtractor: TileTextureExtractor
    getGrassSway?: () => GrassSway | undefined
    // The floor this renderer draws, which its blades are registered under (see GrassFloor).
    grassFloor?: GrassFloor
    depthSortedRoofs?: boolean
}

export class CompositeTileRenderer {
    private waterRocks = new Map<Sprite, WaterContact & { key: string }>()
    clearWaterRocks = (): void => this.waterRocks.clear()
    waterContacts = (): WaterContact[] => {
        const contacts: WaterContact[] = []
        this.waterRocks.forEach((contact, sprite) => {
            if (sprite.destroyed || !this.surfaceObjectsByKey.get(contact.key)?.spriteSet?.includes(sprite)) {
                this.waterRocks.delete(sprite); return
            }
            if (sprite.parent && sprite.visible) contacts.push(contact)
        })
        return contacts
    }
    private bonecallerStones = new Map<Sprite, { shrineId: string; index: number; ritual?: BonecallerRitual; receivedAt: number }>()
    clearBonecallerStones = () => this.bonecallerStones.clear()
    updateBonecallerStones = (now = Date.now()) => {
        if (!this.bonecallerStones.size) return
        const frames = bonecallerStoneFrames(getRendererApp().renderer)
        this.bonecallerStones.forEach((state, sprite) => {
            if (!sprite.parent || sprite.destroyed || !frames.includes(sprite.texture)) {
                this.bonecallerStones.delete(sprite); return
            }
            const ritual = this.logic.entityManager?.get(state.shrineId)?.bonecallerRitual
            if (ritual !== state.ritual) { state.ritual = ritual; state.receivedAt = now }
            const energy = bonecallerStoneEnergy(ritual, state.index, now, now - state.receivedAt)
            sprite.texture = frames[Math.round(energy * (frames.length - 1))]
        })
    }
    private surface: Container
    private skySpriteSurfaceAbove: Container
    private tilemapMetaProvider: TileMapMetaProvider
    private compositeTileSpriteTextures: Map<string, Texture>
    private surfaceObjectsByKey: Map<string, any>
    private attachTexture: (texture: Texture, surface?: Container) => Sprite
    private pertubation: (tileId: string, x: number, y: number, i: number, key: string, multiplier?: number) => number
    private pctChance: (chance: number, key: string) => boolean
    private logic: any
    private tileTextureExtractor: TileTextureExtractor
    // Read lazily rather than held: the renderer that owns the wind is built after this one.
    private getGrassSway: () => GrassSway | undefined
    private grassFloor: GrassFloor | undefined
    private depthSortedRoofs: boolean

    constructor(options: CompositeTileRendererOptions) {
        this.surface = options.surface
        this.skySpriteSurfaceAbove = options.skySpriteSurfaceAbove
        this.tilemapMetaProvider = options.tilemapMetaProvider
        this.compositeTileSpriteTextures = options.compositeTileSpriteTextures
        this.surfaceObjectsByKey = options.surfaceObjectsByKey
        this.attachTexture = options.attachTexture
        this.pertubation = options.pertubation
        this.pctChance = options.pctChance
        this.logic = options.logic
        this.tileTextureExtractor = options.tileTextureExtractor
        this.getGrassSway = options.getGrassSway || (() => undefined)
        this.grassFloor = options.grassFloor
        this.depthSortedRoofs = options.depthSortedRoofs ?? false
    }

    setSurface = (surface: Container): void => {
        this.surface = surface
    }

    /** Offer a piece of vegetation to the wind, under the floor this renderer draws. */
    private offerToWind = (sprite: Sprite, x: number, y: number, textureId: string, heightAbove = 0, entityTileId?: string): void => {
        this.getGrassSway()?.register(sprite, x, y, textureId, heightAbove, entityTileId, this.grassFloor)
    }

    public updateTilemapMetaProvider(tilemapMetaProvider: TileMapMetaProvider): void {
        this.tilemapMetaProvider = tilemapMetaProvider
    }

    // Plant art does not own pointer events. Desktop strikes can still resolve a blade to its
    // root by checking its geometry separately from Pixi's interactive hit test.
    public updateCompositeTilePointer = (at: IPointData) => {
        let root: { x: number; y: number } | undefined
        let topZ = -Infinity
        for (const object of this.surfaceObjectsByKey.values()) {
            if (!object.compositeRoot) continue
            for (const sprite of object.spriteSet as Sprite[]) {
                if (sprite.parent && sprite.worldVisible && sprite.zIndex >= topZ && sprite.containsPoint(at)) {
                    root = object.compositeRoot
                    topZ = sprite.zIndex
                }
            }
        }
        this.logic.playerController.updateCompositeTile(root?.x ?? -1, root?.y ?? -1)
    }

    public renderCompositeTile = (
        tilemapMetaProvider: TileMapMetaProvider,
        parentTileId: string,
        compositeTileId: string,
        x: number,
        y: number,
        zIndex: number = 0,
    ) => {
        const key = `${parentTileId}-${x}-${y}`
        const parentTileMeta = tilemapMetaProvider.resolveTileMeta(parentTileId)
        // An organic cave's rock is painted as one continuous mass by RockMassLayer, not a block per cell.
        if (parentTileMeta.entityTileId === "testore" && tilemapMetaProvider.getOrganicRockLayer?.()) return
        if (parentTileMeta.entityTileId === "bonecaller-stone") {
            if (this.surfaceObjectsByKey.has(key)) return
            const frames = bonecallerStoneFrames(getRendererApp().renderer)
            const sprite = this.attachTexture(frames[0], this.surface)
            sprite.position.set(x * TileSize, y * TileSize - 6 - this.hillLiftAt(tilemapMetaProvider, x, y))
            sprite.zIndex = 100 + y * TileSize + 16
            const context = tilemapMetaProvider.getMapDeltaAt(x, y, tilemapMetaProvider.resolveEntityTileLayerId())?.context
            this.bonecallerStones.set(sprite, { shrineId: context?.bonecallerShrineId,
                index: context?.bonecallerStoneIndex ?? 0, receivedAt: Date.now() })
            this.surfaceObjectsByKey.set(key, { spriteSet: [sprite], compositeTileId,
                tilesetId: "bonecaller-stones", compositeRoot: { x, y } })
            return
        }

        //
        // dense microtiles
        //
        if (parentTileMeta.tileTypes.includes("dense")) {
            this.renderDenseMicrotiles(key, parentTileId, parentTileMeta, x, y, zIndex)
            return
        }

        //
        // compositeTile
        //

        const tileset = tilemapMetaProvider.resolveTileSet(parentTileId)

        const compositeSprites = this.handleRenderCompositeTile(
            key,
            tilemapMetaProvider,
            compositeTileId,
            tileset.tilesetId,
            x,
            y,
            this.surface,
            zIndex,
        )
        const object = this.surfaceObjectsByKey.get(key)
        // Saved HP must reach the damage pass even on a fresh client with no hit history.
        // Trees use trunk notches rather than authored breakage overlays.
        if (object && (parentTileMeta.breakageMeta || isTreeEntityTile(parentTileMeta.entityTileId))) {
            object.delta = tilemapMetaProvider.findMapDeltaAt(x, y)
        }
    }

    public handleRenderCompositeTile = (
        key: string,
        tilemapMetaProvider: TileMapMetaProvider,
        compositeTileId: string,
        tilesetId: string,
        x: number = 0,
        y: number = 0,
        surface?: Container,
        extraZIndex?: number,
    ): Sprite[] => {
        let set: Sprite[] = this.surfaceObjectsByKey.get(key)?.spriteSet
        if (set) {
            return set
        }
        set = []

        const compositeTileMeta = tilemapMetaProvider.resolveCompositeTileIdTileMeta(compositeTileId, tilesetId)
        if (!compositeTileMeta) {
            return set
        }

        const parentTileSet = tilemapMetaProvider.getTilesetById(tilesetId)
        const parentTileMeta = compositeTileMeta.tileMeta

        //
        // compositeTile
        //
        const { width } = parentTileSet

        // find the parent tile location
        let parentTileSetX
        let parentTileSetY

        const tileNumber = compositeTileMeta.tileNumber - 1
        parentTileSetX = tileNumber % width
        parentTileSetY = Math.floor(tileNumber / width)

        const isDenseVertical = parentTileMeta.tileTypes.includes("denseVertical")
        if (isDenseVertical) {
            this.renderDenseVerticalTiles(
                key,
                compositeTileMeta,
                parentTileSet,
                parentTileSetX,
                parentTileSetY,
                x,
                y,
                surface,
                extraZIndex,
                set,
            )
            this.surfaceObjectsByKey.set(key, {
                spriteSet: set,
                compositeTileId,
                tilesetId,
                compositeRoot: { x, y },
            })
        } else {
            const fullTileId = `${parentTileSet.tilesetId}-${compositeTileId}`

            let texture = this.tileTextureExtractor.extract(
                tilemapMetaProvider,
                parentTileSet.tilesetId,
                compositeTileId,
            )

            const bounds = TileTextureExtractor.compositeTileSpriteBounds.get(fullTileId)
            const children: NumberedTileMeta[] = compositeTileMeta.children || []
            let intersects = false
            for (let i = 0; !this.depthSortedRoofs && i < children.length; i++) {
                const childTileMeta = children[i]
                const tileNumber = childTileMeta.tileNumber - 1
                const tileSetX = tileNumber % width
                const tileSetY = Math.floor(tileNumber / width)

                const childTileSetXOffset = tileSetX - parentTileSetX
                const childTileSetYOffset = tileSetY - parentTileSetY

                const xx = x + childTileSetXOffset
                const yy = y + childTileSetYOffset

                if (tilemapMetaProvider.isSkyTileAt(xx, yy)) {
                    intersects = true
                    break
                }
            }

            if (!intersects) {
                const material = parentTileMeta.entityTileId === "testore"
                    ? tilemapMetaProvider.getMapDeltaAt?.(x, y, tilemapMetaProvider.resolveEntityTileLayerId())?.context?.oreMaterial
                    : undefined
                if (isOreMaterial(material)) texture = oreMineralTexture(texture, material, getRendererApp().renderer)
                // The grass that rings a tree's roots (and a wall's base) rings the camp's bushes too. On
                // bare dirt a tree's foot is heaped with soil instead.
                const tree = isTreeEntityTile(parentTileMeta.entityTileId)
                if (tree || isEmberBushComposite(compositeTileId)) {
                    const edges = treeRootEdges(tilesetId, compositeTileId, width, children)
                    if (edges?.length) {
                        const placement = compositeSpritePlacement(compositeTileMeta, bounds, x, y, extraZIndex)
                        const ground = rootGroundPoint(edges)
                        const groundX = placement.x + ground.x, groundY = placement.y + ground.y
                        if (grassAt(tilemapMetaProvider, groundX, groundY)) {
                            texture = treeRootFringeTexture(texture, edges, getRendererApp().renderer, tilemapMetaProvider, groundX, groundY)
                        } else if (tree) {
                            const zone = dirtZoneAt(tilemapMetaProvider, groundX, groundY)
                            if (zone !== undefined) texture = treeRootSoilTexture(texture, edges, zone, getRendererApp().renderer)
                        }
                    }
                }
                this.renderNonIntersectingCompositeTile(
                    compositeTileMeta,
                    texture,
                    bounds,
                    x,
                    y,
                    surface,
                    extraZIndex,
                    set,
                )

            } else {
                this.renderSkyIntersectingCompositeTile(
                    compositeTileMeta,
                    parentTileSet,
                    x,
                    y,
                    surface,
                    extraZIndex,
                    set,
                    bounds,
                    children,
                    tilemapMetaProvider,
                )
            }

            this.surfaceObjectsByKey.set(key, {
                spriteSet: set,
                compositeTileId,
                tilesetId,
            })
            // Short tufts use the ordinary composite path. Offer their complete sprite to wind
            // as well; GrassSway ignores every texture outside its explicit vegetation list.
            for (const sprite of set) this.offerToWind(sprite, x, y, `${tilesetId}-${tileNumber + 1}`, 0, parentTileMeta?.entityTileId)
        }

        // On a hill (openspec world/hills) a tile stands on the ground under its cell, drawn up by its height there; it
        // keeps the depth it has on flat ground, as a body does. Before the wind hinge below is read off the sprites.
        const lift = this.hillLiftAt(tilemapMetaProvider, x, y)
        for (const sprite of set) {
            sprite.y -= lift
            // Pooled sprites keep custom props: always stamp it, so shade sampling can find the ground again.
            ;(sprite as HillLifted).hillLift = lift
        }

        // Rocks keep their authored art and participate in the same waterline/ripples as bodies.
        if (/^(beach-rock-medium-[a-d]|beach-boulder-[a-d]|sea-rock-[a-c]|rock[12]|boulder[1-5])$/.test(compositeTileId) &&
            tilemapMetaProvider.getRiver?.() && set.length === 1) {
            const sprite = set[0]
            const edges = treeRootEdges(tilesetId, compositeTileId, width, compositeTileMeta.children || [])
            const foot = edges?.length ? rootGroundPoint(edges) : { x: sprite.texture.width / 2, y: sprite.texture.height - 1 }
            const current = riverSample(tilemapMetaProvider.getRiver(), (sprite.x + foot.x) / TileSize, (sprite.y + foot.y + lift) / TileSize)
            this.waterRocks.set(sprite, { submergeArt: line => submergedRockTexture(tilesetId, compositeTileId, width, compositeTileMeta.children || [], line), flowX: current.dx, flowY: current.dy, key, id: `water-rock:${key}`, x: sprite.x + foot.x,
                y: sprite.y + foot.y, lift, body: sprite, art: sprite, stationary: true,
                depthOrder: sprite.zIndex, radius: Math.max(5, Math.min(25, sprite.texture.width * .29)),
                maxSink: Math.max(3, Math.min(12, sprite.texture.height * .25)) })
        }

        // Keep the wind hinge at the actual roots, above any transparent padding or baked shadow.
        if (isTreeEntityTile(parentTileMeta?.entityTileId) && set.length) {
            const edges = treeRootEdges(tilesetId, compositeTileId, width, compositeTileMeta.children || [])
            const top = Math.min(...set.map(s => s.y))
            const bottom = Math.max(...set.map(s => s.y + s.texture.height * Math.abs(s.scale.y)))
            this.surfaceObjectsByKey.get(key).treeWind = {
                x: x * TileSize,
                y: edges?.length ? top + rootGroundPoint(edges).y : bottom,
            }
        }

        // Tag the tiles the occluder-fade may fade, with the gate policy each kind wants (a structured
        // check on the tile's entityTileId -- no sprite/texture name parsing). Pool sprites are untagged
        // at checkout (SpriteManagementService.attachTexture), so this only needs to stamp the few that
        // fade. A tile's BREAKAGE overlay comes back through here under its own composite id
        // (stoneBreaklevel*, no fade policy) and is then re-parented onto the block it cracks, which is
        // what we want: a pixi child inherits its parent's alpha, and a child's x/y are parent-relative,
        // so the occlusion box math must only ever see the top-level sprite.
        const policy = occluderFadePolicy(parentTileMeta?.entityTileId)
        if (policy) {
            for (const g of set) {
                ;(g as any).occluderFade = policy
            }
        }

        return set
    }

    /** How far a tile on cell (x, y) is drawn up for the hill under it: the height at the foot of its cell. */
    private hillLiftAt = (provider: TileMapMetaProvider | undefined, x: number, y: number): number =>
        hillHeightOf(provider ?? this.tilemapMetaProvider, (x + 0.5) * TileSize, (y + 1) * TileSize - 1)

    public renderDenseMicrotiles = (
        key: string,
        parentTileId: string,
        parentTileMeta: any,
        x: number,
        y: number,
        zIndex: number,
    ) => {
        const set = []
        this.surfaceObjectsByKey.set(key, {
            spriteSet: set,
        })

        const fullTileId = this.tilemapMetaProvider.resolveTilesetTileId(parentTileId)
        const cacheKey = `${contentTextureGeneration()}:${fullTileId}`
        let textTure = this.compositeTileSpriteTextures.get(cacheKey)
        if (!textTure) {
            if (parentTileMeta.spriteId) {
                let resource = contentSheetResource("humanoid")
                if (resource) {
                    textTure = resource.texture
                }
            } else {
                textTure = contentTexture(`${fullTileId}`)
                this.compositeTileSpriteTextures.set(cacheKey, textTure)
            }
        }

        const parentFine = roughToFineCoordinates({ x, y })
        const noiseChance = 0.1

        const lift = this.hillLiftAt(this.tilemapMetaProvider, x, y)
        const print = (offsetX: number = 0, offsetY: number = 0) => {
            const g = this.attachTexture(textTure)
            g.x = parentFine.x + offsetX
            g.y = parentFine.y + offsetY - lift
            g.zIndex = zIndex
            set.push(g)
        }

        print()
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(-0.5 * TileSize, -0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(0.5 * TileSize, -0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(-0.5 * TileSize, 0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(0.5 * TileSize, 0.5 * TileSize)
    }

    /**
     * How far each copy of the dense-vertical tile `parentTileId` at (x, y) is drawn above its place: one
     * entry per copy, as renderDenseVerticalTiles draws them (its shadow is cast from the same numbers).
     */
    public denseCopyLifts = (parentTileId: string, tileMeta: { denseCopies?: number } | undefined, x: number, y: number): number[] =>
        this.copyLifts(`${parentTileId}-${x}-${y}`, tileMeta, x, y)

    private copyLifts = (key: string, tileMeta: { denseCopies?: number } | undefined, x: number, y: number): number[] => {
        // The old LPC long grass is flat stripes, stacked 1-3 deep to look thick. A tile can name its own
        // count instead: a drawn clump (the ember meadow's) stacked twice is the same picture twice,
        // a few pixels apart, which reads as an echo rather than as more grass.
        const count = tileMeta?.denseCopies ?? (this.pctChance(10, `dense-vertical-${key}-1`)
            ? this.pctChance(50, `dense-vertical-${key}-2`)
                ? 1
                : 3
            : 2)
        return Array.from({ length: count }, (_, i) => this.pertubation("_", x, y, i, `horizontalDensity`))
    }

    public renderDenseVerticalTiles = (
        key: string,
        compositeTileMeta: any,
        parentTileSet: any,
        parentTileSetX: number,
        parentTileSetY: number,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
    ) => {
        const lifts = this.copyLifts(key, compositeTileMeta.tileMeta, x, y)
        const count = lifts.length
        const gradient = TileSize / count

        for (let i = 0; i < count; i++) {
            let verticalOffset = i * gradient
            const yPertubation = lifts[i]

            const children: NumberedTileMeta[] = compositeTileMeta.children || []
            const parentTileIdNumber = compositeTileMeta.tileNumber
            // The id the composite's parent tile renders under. NOTE the convention change: a tile's
            // position as an AUTHOR writes it is one below the id the client resolves it to (see
            // flora.ts), but compositeTileMeta.tileNumber is already in the resolved convention. Both
            // numbers are small, plausible and name a real neighbouring tile, so getting this wrong
            // matches nothing and reports nothing -- which is exactly what it did first.
            const parentCompositeTextureId = `${parentTileSet.tilesetId}-${parentTileIdNumber}`

            let height = 0
            children.forEach(childTileMeta => {
                const tileId = `${childTileMeta.tileNumber}`
                const tileNumber = childTileMeta.tileNumber - 1
                const tileSetX = tileNumber % parentTileSet.width
                const tileSetY = Math.floor(tileNumber / parentTileSet.width)

                const fullTileId = `${parentTileSet.tilesetId}-${tileId}`

                const isParent = parentTileIdNumber === tileNumber
                const childTileSetXOffset = tileSetX - parentTileSetX
                const childTileSetYOffset = tileSetY - parentTileSetY
                if (!isParent) {
                    height = Math.max(height, Math.abs(childTileSetYOffset))
                }

                const cacheKey = `${contentTextureGeneration()}:${fullTileId}`
                let textTure = this.compositeTileSpriteTextures.get(cacheKey)
                if (!textTure) {
                    textTure = contentTexture(`${fullTileId}`)
                    this.compositeTileSpriteTextures.set(cacheKey, textTure)
                }
                const g = this.attachTexture(textTure, surface)

                const fineItem = CoordinatePool.instance.checkout()
                const roughItem = CoordinatePool.instance.checkout()
                const rough = roughItem.data
                rough.x = x + childTileSetXOffset
                rough.y = y + childTileSetYOffset
                const fine = fineItem.data
                setRoughToFineCoordinates(rough, fine)

                g.x = fine.x - this.pertubation(tileId, fine.x, fine.y, i, `verticalDensity`, 1)
                g.y = fine.y - yPertubation
                g.zIndex =
                    100 +
                        roughToFineCoordinates({ x, y }).y -
                        verticalOffset -
                        yPertubation +
                        TileSize / 3 +
                        extraZIndex || 0

                CoordinatePool.instance.release(roughItem)
                CoordinatePool.instance.release(fineItem)

                g.interactive = false

                // Offer this sub-tile to the wind. Long grass is a two-tile vertical composite -- a
                // base and a top -- so the sub-tile's own height above the anchor row is exactly how
                // much of the bend it should carry: the base barely moves and the top does the
                // leaning, which is how a plant bends rather than how a decal slides.
                //
                // Keyed on the COMPOSITE's parent tile, not on the sub-tile, so one entry in the sway
                // table covers every piece of the plant.
                this.offerToWind(g, x, y, parentCompositeTextureId, -childTileSetYOffset, compositeTileMeta.tileMeta?.entityTileId)

                set.push(g)
            })
            if (height > 0 || compositeTileMeta.tileMeta.renderZIdxOffset !== undefined) {
                set.forEach(g => {
                    if (height > 1) {
                        g.y += TileSize
                        g.zIndex += TileSize
                    }
                    if (compositeTileMeta.tileMeta.renderZIdxOffset) {
                        g.zIndex += compositeTileMeta.tileMeta.renderZIdxOffset
                    }
                })
            }
        }
    }

    public renderNonIntersectingCompositeTile = (
        compositeTileMeta: any,
        texture: Texture,
        bounds: Bounds,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
    ) => {
        const surfaceToUse = surface || this.surface
        const containingSprite: Sprite = this.attachTexture(texture, surfaceToUse)
        const placement = compositeSpritePlacement(compositeTileMeta, bounds, x, y, extraZIndex)

        containingSprite.x = placement.x
        containingSprite.y = placement.y
        containingSprite.zIndex = placement.zIndex

        set.push(containingSprite)
    }

    public renderSkyIntersectingCompositeTile = (
        compositeTileMeta: any,
        parentTileSet: any,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
        bounds: Bounds,
        children: NumberedTileMeta[],
        tilemapMetaProvider: TileMapMetaProvider,
    ) => {
        // find the parent tile location
        const tileNumber = compositeTileMeta.tileNumber - 1
        const parentTileSetXLocal = tileNumber % parentTileSet.width
        const parentTileSetYLocal = Math.floor(tileNumber / parentTileSet.width)

        const fineXX = roughToFineCoordinates({ x, y }).x
        // Splitting the artwork around sky tiles must retain the whole composite's ground depth.
        const placement = compositeSpritePlacement(compositeTileMeta, bounds, x, y, extraZIndex)
        const left = Math.min(...children.map(child => (child.tileNumber - 1) % parentTileSet.width))
        const top = Math.min(...children.map(child => Math.floor((child.tileNumber - 1) / parentTileSet.width)))

        children.forEach(childTileMeta => {
            const tileId = `${childTileMeta.tileNumber}`
            const tileNumber = childTileMeta.tileNumber - 1
            const tileSetX = tileNumber % parentTileSet.width
            const tileSetY = Math.floor(tileNumber / parentTileSet.width)

            const fullTileId = `${parentTileSet.tilesetId}-${tileId}`
            const childTileSetXOffset = tileSetX - parentTileSetXLocal
            const childTileSetYOffset = tileSetY - parentTileSetYLocal

            const xx = x + childTileSetXOffset
            const yy = y + childTileSetYOffset

            const skyAt = tilemapMetaProvider.isSkyTileAt(xx, yy)
            const surfaceToUse = skyAt ? this.skySpriteSurfaceAbove : surface
            const subtileSprite = this.attachTexture(contentTexture(fullTileId), surfaceToUse)
            const fineX = placement.x + (tileSetX - left) * TileSize
            const fineY = placement.y + (tileSetY - top) * TileSize

            subtileSprite.x = fineX
            subtileSprite.y = fineY

            subtileSprite.zIndex = placement.zIndex

            set.push(subtileSprite)

            if (false) {
                if (!skyAt) {
                    {
                        const g = new Graphics()
                        g.beginFill(0xfffffff)
                        g.drawCircle(16, 16, 5)
                        g.zIndex = 10000000
                        g.endFill()
                        g.x = fineXX
                        g.y = subtileSprite.zIndex - 100
                        surfaceToUse.addChild(g)
                    }

                    const g = new Graphics()
                    g.beginFill(0xff0000)
                    g.drawCircle(16, 16, 5)
                    g.zIndex = 10000000
                    g.endFill()
                    g.x = fineX
                    g.y = fineY
                    surfaceToUse.addChild(g)
                } else {
                    const g = new Graphics()
                    g.beginFill(0x00ff00)
                    g.drawCircle(16, 16, 5)
                    g.zIndex = 10000000
                    g.endFill()
                    g.x = fineX
                    g.y = fineY
                    surfaceToUse.addChild(g)
                }
            }
        })
    }

    updateSkySurfaces = (skySpriteSurfaceAbove: Container) => {
        this.skySpriteSurfaceAbove = skySpriteSurfaceAbove
    }
}
